import { type Database } from "@fitness-app/supabase";

export type ProductResourceEntity = Database["public"]["Tables"]["product_resource"]["Row"];
export type ProductResourceCategory = Database["public"]["Tables"]["product_resource_category"]["Row"];
export type ProductResourceStatusEnum = Database["public"]["Enums"]["ProductResourceStatus"];
export type ProductResourceTypeEnum = Database["public"]["Enums"]["ProductResourceType"];

export const ProductResourceStatus: Record<
  Database["public"]["Enums"]["ProductResourceStatus"],
  Database["public"]["Enums"]["ProductResourceStatus"]
> = {
  DRAFT: "DRAFT",
  AVAILABLE: "AVAILABLE",
  UNAVAILABLE: "UNAVAILABLE",
  IN_REVIEW: "IN_REVIEW",
} as const;

/**
 * What a client may see in the materials list of a product they bought.
 *
 * IN_REVIEW (TRE-1962) is the step between a draft and a published material: ready to be looked
 * at, but not by everyone yet. It is handed to the group carrying the PostHog
 * `hasAccessToInReviewResources` flag, so the test audience is picked in PostHog instead of in the
 * database. Both client apps ask the same question here — a list that filters statuses by hand
 * drifts the moment a status is added.
 */
export const clientVisibleResourceStatuses = (canSeeInReview: boolean): ProductResourceStatusEnum[] =>
  canSeeInReview
    ? [ProductResourceStatus.AVAILABLE, ProductResourceStatus.IN_REVIEW]
    : [ProductResourceStatus.AVAILABLE];

export const ProductResourceType: Record<
  Database["public"]["Enums"]["ProductResourceType"],
  Database["public"]["Enums"]["ProductResourceType"]
> = {
  IN_APP: "IN_APP",
  NOTION: "NOTION",
} as const;

export type ProductResourceBase = ProductResourceEntity;

export interface InAppResource extends ProductResourceBase {
  attachments: { url: string; name: string; uid: string }[];
  type: "IN_APP";
}

export type InAppResourcePayload = Omit<InAppResource, "id" | "createdAt" | "updatedAt" | "notifications">;

export type ProductResource = InAppResource;
